import 'react-cmdk/dist/cmdk.css'

import { useEffect, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/dist/query'
import CommandPalette, { filterItems, getItemIndex, JsonStructureItem } from 'react-cmdk'
import { useGetInstructorsQuery, useSearchClassroomQuery } from '@Features/LandingPage/LandingPageApi/LandingPageApi'
import { logoutRequestAction } from '@Features/Authentication/AuthenticationSlice/Authentication.slice'
import { useAppDispatch } from '@Hooks/ReduxHooks'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { useNavigate } from 'react-router'
import Spacer from '@Components/Utilities/Spacer'
import routes from '@Constants/Routes'
import { Typography } from '@eduact/ed-system'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@eduact/student-theme'
import Fuse from 'fuse.js'
import usePlatformFlags from '@Hooks/usePlatformFlags'

type Props = {
	open: boolean
	onClose: (_: boolean) => void
}
export const Commands = (props: Props) => {
	const [search, setSearch] = useState('')
	const [page, setPage] = useState('root')
	// Debounce the server query so we don't fire a request on every keystroke,
	// and skip entirely when the box is empty.
	const [debouncedSearch, setDebouncedSearch] = useState('')
	useEffect(() => {
		const id = setTimeout(() => setDebouncedSearch(search), 300)
		return () => clearTimeout(id)
	}, [search])
	const { data } = useSearchClassroomQuery(debouncedSearch ? { searchQuery: debouncedSearch } : skipToken)
	const { data: instructors } = useGetInstructorsQuery(undefined, {
		selectFromResult: ({ data }) => {
			if (!search) return { data: data?.data || [] }
			const fuse = new Fuse(data?.data!, {
				keys: ['first_name', 'last_name'],
				shouldSort: true,
				threshold: 0.3,
				distance: 100,
			})
			return { data: fuse.search(search).map(_ => _.item) as unknown as BriefInstructor[] }
		},
	})
	const isAuth = useAppSelector(s => s.auth.isAuth)
	const { classroomsEnabled } = usePlatformFlags()
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const [t, i18n] = useTranslation('layouts', { keyPrefix: 'command_bar' })
	const { setTheme, theme } = useTheme()

	const changeLanguage = () => {
		i18n.changeLanguage(i18n.language === 'ar-EG' ? 'en-GB' : 'ar-EG')
		if (i18n.language === 'ar-EG') {
			setTheme?.({ ...theme, direction: 'rtl' })
		} else {
			setTheme?.({ ...theme, direction: 'ltr' })
		}
	}

	const filteredItems = filterItems(
		[
			{
				id: 'search',
				heading: t('placeholder'),
				items: [
					...(search
						? [
								{
									id: 'see-all-results',
									icon: 'MagnifyingGlassIcon',
									children: `${t('see_all_results', { defaultValue: 'عرض كل النتائج عن' })}: ${search}`,
									closeOnSelect: true,
									onClick: () => {
										navigate(`/search?query=${encodeURIComponent(search)}`)
										props.onClose(false)
									},
								} as JsonStructureItem,
						  ]
						: []),
					// Classroom search only exists on the classrooms platform.
					...(classroomsEnabled
						? [
								{
									id: 'classrooms',
									icon: 'BookOpenIcon',
									children: t('find_a_classroom'),
									onClick: () => {
										setPage('classrooms')
									},
									closeOnSelect: false,
								} as JsonStructureItem,
						  ]
						: []),
					{
						id: 'instructors',
						icon: 'UserCircleIcon',
						children: t('find_a_instructor'),
						onClick: () => {
							setPage('instructors')
						},
						closeOnSelect: false,
					},
				],
			},
			{
				id: 'dashboard',
				heading: t('dashboard'),
				items: [
					// The dashboard overview is classroom analytics — classic/hybrid only.
					...(classroomsEnabled
						? [
								{
									id: 'dashboard_overview',
									icon: 'RectangleGroupIcon',
									children: t('dashboard'),
									onClick: () => {
										navigate('/dashboard')
									},
								} as JsonStructureItem,
						  ]
						: []),
					{
						id: 'profile',
						icon: 'UserCircleIcon',
						children: t('profile'),
						onClick: () => {
							navigate(routes.account)
						},
					},
					{
						id: 'settings',
						icon: 'CogIcon',
						children: t('settings'),
						onClick: () => {
							navigate(routes.accountDevices)
						},
					},
				],
			},
			{
				id: 'shortcuts',
				heading: t('shortcuts'),
				items: [
					{
						id: 'lang',
						icon: 'LanguageIcon',
						children: <Typography color="white">{t('language')}</Typography>,
						onClick: changeLanguage,
					},
				],
			},
		],
		search
	)

	const backAction: JsonStructureItem = {
		id: 'back',
		title: t('back'),

		children: (
			<Typography color="white" py="0.2rem">
				{t('back')}
			</Typography>
		),
		icon: i18n.language === 'ar-EG' ? 'ChevronRightIcon' : 'ChevronLeftIcon',
		showType: false,
		closeOnSelect: false,
		onClick: () => {
			setPage('root')
		},
	}

	return (
		<>
			<CommandPalette
				isOpen={props.open}
				onChangeOpen={() => {
					props.onClose(false)
					setPage('root')
				}}
				onChangeSearch={setSearch}
				search={search}
				page={page}
				placeholder={`${t('placeholder')}...`}
				footer={
					<div>
						{isAuth && (
							<CommandPalette.ListItem
								index={0}
								onClick={async () => {
									// Awaited so the server call is not cancelled by the
									// navigation that follows it.
									await dispatch(logoutRequestAction())
									navigate('/')
									props.onClose(false)
								}}
								showType={false}
							>
								<Spacer py="0.2rem">
									<Typography color={'white'}>{t('logout')}</Typography>
								</Spacer>
							</CommandPalette.ListItem>
						)}
					</div>
				}
			>
				<CommandPalette.Page id="root">
					{filteredItems.map(item => (
						<CommandPalette.List key={item.id} heading={item.heading}>
							{item.items.map(_item => (
								<CommandPalette.ListItem
									{..._item}
									key={_item.id}
									index={getItemIndex(filteredItems, _item.id)}
									title={_item.title}
									showType={false}
								>
									{_item.children}
								</CommandPalette.ListItem>
							))}
						</CommandPalette.List>
					))}
				</CommandPalette.Page>
				<CommandPalette.Page id="classrooms" onEscape={() => setPage('root')}>
					<CommandPalette.List>
						<CommandPalette.ListItem index={0} {...backAction} />
						{data?.data.map((item, index) => (
							<CommandPalette.ListItem
								key={item.id}
								index={index + 1}
								showType={false}
								onClick={() => {
									navigate(`/classroom/${item.label}`)
									props.onClose(false)
								}}
							>
								{item.title}
							</CommandPalette.ListItem>
						))}
					</CommandPalette.List>
				</CommandPalette.Page>
				<CommandPalette.Page id="instructors" onEscape={() => setPage('root')}>
					<CommandPalette.List>
						<CommandPalette.ListItem index={0} {...backAction} />
						{instructors?.map((item, index) => (
							<CommandPalette.ListItem
								key={item.instructor?.label}
								index={index + 1}
								showType={false}
								onClick={() => {
									navigate(`/instructor/${item.instructor?.label}`)
									props.onClose(false)
								}}
							>
								<Typography color="white">
									{item.first_name} {item.last_name}
								</Typography>
							</CommandPalette.ListItem>
						))}
					</CommandPalette.List>
				</CommandPalette.Page>
			</CommandPalette>
		</>
	)
}
